import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import PublicLayout from "@/components/PublicLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  FileText, Receipt, Building2, UtensilsCrossed, ShieldCheck,
  Clock, IndianRupee, Users, ArrowRight, CheckCircle2,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const HERO_IMG = "https://images.unsplash.com/photo-1707157284454-553ef0a4ed0d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxmaW5hbmNpYWwlMjBkb2N1bWVudHMlMjBjaGFydHMlMjBkZXNrfGVufDB8fHx8MTc4NjI5MjY0OXww&ixlib=rb-4.1.0&q=85";

const ICON_MAP = { FileText, Receipt, Building2, UtensilsCrossed };

const homeMinPrice = (svc) => {
  const plans = svc.plans || [];
  const prices = plans
    .flatMap((p) => {
      if (p.year_prices) return Object.values(p.year_prices).map(Number).filter((n) => n > 0);
      return typeof p.price === "number" ? [p.price] : [];
    })
    .filter((n) => n > 0);
  if (prices.length) return Math.min(...prices);
  return Number(svc.price || 0);
};

const STEPS = [
  { n: "01", t: "Pick a Service", d: "Choose from ITR, GST, Udyam, FSSAI and more." },
  { n: "02", t: "Share Details", d: "Fill a short form and upload documents securely." },
  { n: "03", t: "We Process", d: "Our experts prepare and file with authorities." },
  { n: "04", t: "Track & Receive", d: "Track live status and receive digital certificates." },
];

const WHY = [
  { icon: ShieldCheck, t: "Secure & Compliant", d: "Bank-grade encryption. Documents encrypted at rest." },
  { icon: Clock, t: "On-Time Filing", d: "SLAs on every service — no last-minute surprises." },
  { icon: IndianRupee, t: "Transparent Pricing", d: "One flat fee. No hidden charges, ever." },
  { icon: Users, t: "Expert Backed", d: "CA-supported review on every ITR & GST filing." },
];

export default function Home() {
  const [services, setServices] = useState([]);
  useEffect(() => {
    api.get("/services?home_only=true").then((r) => setServices(r.data.services || [])).catch(() => {});
  }, []);
  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative gradient-radial-blue">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 text-xs font-medium text-[#1E3A8A]">
              <span className="w-2 h-2 rounded-full bg-emerald-500" /> Trusted by 25,000+ Indian filers
            </div>
            <h1 className="mt-6 text-[42px] sm:text-[56px] lg:text-[68px] font-display font-bold text-[#0A192F] tracking-[-0.02em] leading-[1.02]">
              Effortless tax filings.
              <br />
              <span className="text-[#1E3A8A]">Made for India.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed">
              File ITR, register GST, get your Udyam certificate or FSSAI license — expert-reviewed and delivered on time. One dashboard for your entire compliance.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/services">
                <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF] h-12 px-6 text-base" data-testid="hero-get-started-btn">
                  Get Started <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/track-order">
                <Button variant="outline" className="h-12 px-6 text-base" data-testid="hero-track-btn">Track my Order</Button>
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-600">
              {["100% Digital", "CA Verified", "24-hour ITR filing", "Refund Assurance"].map((f) => (
                <div key={f} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> {f}</div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="absolute -inset-6 bg-gradient-to-tr from-blue-100/40 to-transparent rounded-3xl blur-2xl" />
            <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-lg">
              <img src={HERO_IMG} alt="Tax documents" className="w-full h-[380px] object-cover" />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-xl border border-slate-200 p-4 flex items-center gap-4">
                <div className="w-11 h-11 rounded-lg bg-[#1E3A8A] text-white flex items-center justify-center font-bold font-display">₹</div>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-widest text-slate-500">Avg. Refund</div>
                  <div className="text-lg font-bold text-[#0A192F]">₹ 18,450 <span className="text-sm font-normal text-slate-500">/ ITR</span></div>
                </div>
                <div className="status-badge badge-success">Live</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="section-label">Our Services</div>
              <h2 className="mt-2 font-display font-bold text-3xl sm:text-4xl text-[#0A192F]">Everything you need to stay compliant</h2>
            </div>
            <Link to="/services" className="text-[#1E3A8A] font-medium text-sm hover:underline">View all services →</Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {services.map((s) => {
              const Icon = ICON_MAP[s.icon] || FileText;
              const fromPrice = homeMinPrice(s);
              return (
                <Link
                  key={s.slug}
                  to={`/service/${s.slug}`}
                  className="card-crisp rounded-xl p-5 block group"
                  data-testid={`home-service-${s.slug}`}
                >
                  <div className="w-11 h-11 rounded-lg bg-blue-50 text-[#1E3A8A] flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="font-display font-semibold text-lg text-[#0A192F]">{s.name}</div>
                  <div className="text-sm text-slate-600 mt-1.5 leading-relaxed line-clamp-3">{s.short_description}</div>
                  <div className="mt-4 flex items-center justify-between pt-4 border-t border-slate-100">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">From</div>
                      <div className="font-bold text-[#0A192F]">₹{Number(fromPrice).toLocaleString("en-IN")}</div>
                    </div>
                    <div className="text-xs text-slate-500">{s.processing_time}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="section-label">How it works</div>
            <h2 className="mt-2 font-display font-bold text-3xl sm:text-4xl text-[#0A192F]">Four simple steps. Zero paperwork stress.</h2>
          </div>
          <div className="mt-10 grid md:grid-cols-4 gap-5">
            {STEPS.map((s) => (
              <div key={s.n} className="p-6 rounded-xl border border-slate-200 bg-slate-50/50">
                <div className="font-mono text-[#1E3A8A] font-bold text-sm">{s.n}</div>
                <div className="mt-3 font-display font-semibold text-lg text-[#0A192F]">{s.t}</div>
                <div className="mt-1.5 text-sm text-slate-600">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="section-label">Why Way2Efiling</div>
            <h2 className="mt-2 font-display font-bold text-3xl sm:text-4xl text-[#0A192F]">Filing you can trust. Always on time.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Way2Efiling is operated by <span className="font-semibold text-[#0A192F]">SHREE ADARSH INFOTECH</span> — a registered business in Rajasthan (GSTIN <span className="font-mono">08KKOPS0455P1Z2</span>). We serve individuals, freelancers, businesses and startups across India.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {WHY.map((w) => {
                const Icon = w.icon;
                return (
                  <div key={w.t} className="p-4 rounded-xl border border-slate-200 bg-white">
                    <Icon className="w-5 h-5 text-[#1E3A8A]" />
                    <div className="mt-2 font-semibold text-[#0A192F]">{w.t}</div>
                    <div className="mt-1 text-sm text-slate-600">{w.d}</div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { k: "25,000+", v: "Filings completed" },
              { k: "4.8/5", v: "Customer rating" },
              { k: "24 hrs", v: "Avg ITR filing" },
              { k: "PAN India", v: "Service coverage" },
            ].map((s) => (
              <div key={s.k} className="rounded-xl bg-white border border-slate-200 p-6">
                <div className="text-3xl font-display font-bold text-[#1E3A8A]">{s.k}</div>
                <div className="text-sm text-slate-600 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-label">FAQ</div>
          <h2 className="mt-2 font-display font-bold text-3xl sm:text-4xl text-[#0A192F]">Answers to common questions</h2>
          <div className="mt-8">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="q1">
                <AccordionTrigger data-testid="faq-q1">Who can file ITR with Way2Efiling?</AccordionTrigger>
                <AccordionContent>Salaried individuals, freelancers, business owners and NRIs — we support ITR-1 through ITR-4 filings.</AccordionContent>
              </AccordionItem>
              <AccordionItem value="q2">
                <AccordionTrigger>How secure are my documents?</AccordionTrigger>
                <AccordionContent>Documents are stored on secure object storage with per-user isolation. Only authorised staff can access them.</AccordionContent>
              </AccordionItem>
              <AccordionItem value="q3">
                <AccordionTrigger>What is the refund policy?</AccordionTrigger>
                <AccordionContent>If we can&apos;t complete your filing due to reasons on our side, we&apos;ll issue a full refund within 5-7 business days.</AccordionContent>
              </AccordionItem>
              <AccordionItem value="q4">
                <AccordionTrigger>Do you provide GST invoices?</AccordionTrigger>
                <AccordionContent>Yes — every paid order receives a tax invoice from SHREE ADARSH INFOTECH (GSTIN 08KKOPS0455P1Z2).</AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl bg-[#0A192F] text-white p-10 lg:p-14 flex flex-col lg:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display font-bold text-2xl sm:text-3xl">Ready to file?</h3>
              <p className="text-slate-300 mt-2">Get started in under 2 minutes. Cancel anytime before payment.</p>
            </div>
            <div className="flex gap-3">
              <Link to="/register"><Button className="bg-white text-[#0A192F] hover:bg-slate-100 h-12 px-6" data-testid="cta-register">Create Account</Button></Link>
              <Link to="/services"><Button variant="outline" className="border-white/40 text-white hover:bg-white/10 h-12 px-6" data-testid="cta-view-services">Browse Services</Button></Link>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
